import { useState } from "react";
import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  ApiProvider,
  ApprovalProvider,
  AuthProvider,
  CallbackScreen,
  LoginScreen,
  PrincipalProvider,
  makeQueryClient,
  useAuth,
  type OidcAuth,
} from "@csa/web-foundation";
import { I18nProvider, useT } from "./i18n/index";
import { ConsoleShell } from "./shell/console-shell";
import { useApplyPreferences } from "./surfaces/account/apply-preferences";
import { SURFACES } from "./shell/nav";
import { SurfacePlaceholder } from "./surfaces/placeholder";
import { QueuePage } from "./surfaces/queue/queue-page";
import { RecommendationHome } from "./surfaces/recommendation/recommendation-home";
import { RecommendationDetail } from "./surfaces/recommendation/recommendation-detail";
import { HouseholdHome } from "./surfaces/household/household-home";
import { Household360 } from "./surfaces/household/household-360";
import { ClaimsDesk } from "./surfaces/claims/claims-desk";
import { ClaimDetail } from "./surfaces/claims/claim-detail";
import { ReconciliationDashboard } from "./surfaces/reconciliation/reconciliation-dashboard";
import { ApproverInbox } from "./surfaces/approvals/approver-inbox";
import { IssuanceHome } from "./surfaces/issuance/issuance-home";
import { IssuanceDetail } from "./surfaces/issuance/issuance-detail";
import { PhoneIntake } from "./surfaces/intake/phone-intake";
import { MyBook } from "./surfaces/book/my-book";
import { PolicyDetailPage } from "./surfaces/policy/policy-detail";
import { PrepareOffersPage } from "./surfaces/prepare-offers/prepare-offers-page";
import { QuoteFormPage } from "./surfaces/quote-form/quote-form-page";
import { GunumDashboard } from "./surfaces/today/gunum-dashboard";
import { RenewalsPage } from "./surfaces/renewals/renewals-page";
import { NotificationsPage } from "./surfaces/notifications/notifications-page";
import { AccountSettings } from "./surfaces/account/account-settings";
import { RelationshipCockpit } from "./surfaces/relationships/relationship-cockpit";

// Surfaces with a real implementation (AC2+); the rest land on the placeholder.
const SURFACE_VIEWS: Record<string, ReactNode> = {
  queue: <QueuePage />,
  dashboard: <GunumDashboard />,
  recommendations: <RecommendationHome />,
  households: <HouseholdHome />,
  relationships: <RelationshipCockpit />,
  claims: <ClaimsDesk />,
  reconciliation: <ReconciliationDashboard />,
  approvals: <ApproverInbox />,
  issuance: <IssuanceHome />,
  intake: <PhoneIntake />,
  book: <MyBook />,
  teklif: <QuoteFormPage />,
  renewals: <RenewalsPage />,
  notifications: <NotificationsPage />,
  settings: <AccountSettings />,
};

// The authenticated app: principal + shell + the routed (RBAC-gated) surfaces.
function GatedApp() {
  const { status } = useAuth();
  const { t } = useT();
  // D9 — apply the caller's saved accessibility preferences to <html>. This lives
  // HERE, not in ConsoleShell: the hook reads GET /v1/me/preferences, so it needs
  // ApiProvider + QueryClientProvider above it. ConsoleShell is deliberately
  // API-free and its test harness mounts it without those providers — putting the
  // hook there turned every shell test into a "useApi outside ApiProvider" throw.
  useApplyPreferences();
  if (status === "loading") return <main className="center">{t("auth.loading")}</main>;
  if (status === "anonymous") return <LoginScreen />;
  return (
    <PrincipalProvider>
      <ConsoleShell>
        <Routes>
          {SURFACES.map((s) => (
            <Route
              key={s.key}
              path={s.path.slice(1)}
              element={SURFACE_VIEWS[s.key] ?? <SurfacePlaceholder titleKey={s.labelKey} />}
            />
          ))}
          <Route path="leads/:leadId/prepare-offers" element={<PrepareOffersPage />} />
          <Route path="recommendations/:id" element={<RecommendationDetail />} />
          <Route path="households/:id" element={<Household360 />} />
          <Route path="claims/:id" element={<ClaimDetail />} />
          <Route path="issuance/:id" element={<IssuanceDetail />} />
          <Route path="policies/:id" element={<PolicyDetailPage />} />
          <Route path="*" element={<Navigate to={SURFACES[0]!.path} replace />} />
        </Routes>
      </ConsoleShell>
    </PrincipalProvider>
  );
}

// Provider composition (ADR 0015 §5/§6). The OidcAuth instance is the AuthHandle
// the ApiClient consumes AND the SessionAuth the tree boots — one source of truth.
export function App({ auth }: { auth: OidcAuth }) {
  const [queryClient] = useState(makeQueryClient);
  return (
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider auth={auth}>
          <ApprovalProvider>
            <ApiProvider auth={auth}>
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Routes>
                  <Route path="/auth/callback" element={<CallbackScreen auth={auth} />} />
                  <Route path="/*" element={<GatedApp />} />
                </Routes>
              </BrowserRouter>
            </ApiProvider>
          </ApprovalProvider>
        </AuthProvider>
      </QueryClientProvider>
    </I18nProvider>
  );
}
